import type { LogPackage, BatchLog as Logger } from './logger';
import type { BasePlugin, BasePluginConstructor } from '@/plugins/base';
import type { ClientLog as ClientLogV3 } from '../proto/v3/client_log';
import type { CommonPackage, CommonPackageOptions } from './common';
import type { HttpCommonPackage, CommonConfig, UrlObj, PageTypeKey } from '@/types/configuration';
import Global from '../config/global';
import UrlPackage from './url-package';
import EventEmitter from '../util/events';
import initConfig from '@/config';
import { error } from '@/util/console';
import type {
    EventType as IEventType,
    EventOptions as IEventOptions,
    ImmediatelyEventOptions, UserEventType
} from '@/types/event';
import { pageWarning } from '@/util';
import type { UserEventOptions } from '@/types/event';
import type { DefaultGenericType } from '@/types';

let pluginIndex = 0;

export default class Weblog <C extends {
    [key in UserEventType] ?: any
} = DefaultGenericType> extends EventEmitter {
    static __instance: Weblog<{
        [key in UserEventType] ?: any
    }>;
    version: string = '__VERSION__';
    currentUrlPackage!: UrlPackage;
    referUrlPackage?: UrlPackage;
    commonPackage!: CommonPackage;
    baseOption: HttpCommonPackage;
    logConfig: CommonConfig;
    // 核心日志发送
    logger!: Logger;
    // 插件
    plugins: {
        [key: string]: BasePlugin | any;
    } = {};
    /**
     * @param {?object} logConfig Logger初始化配置
     * @param {object=} options 额外配置参数
     *                    - base 公参的覆盖
     */
    constructor(
        logConfig: CommonConfig,
        base?: HttpCommonPackage,
    ) {
        super();
        // 初始化初始化参数配置和埋点基础信息配置
        this.baseOption = { ...base };
        this.logConfig = initConfig(logConfig);
        // 初始化页面信息
        this.initUrlPackage();
        // 记录首个实例
        if (!Weblog.__instance) {
            Weblog.__instance = this;
        }
    }

    get sessionId() {
        return Global.sessionId;
    }

    initUrlPackage() {
        this.updateCurrentUrlPackage();
    }

    /**
     * 更新当前 url 信息
     * @param url
     * @param type
     */
    updateCurrentUrlPackage(url: UrlObj | string = {}, type: PageTypeKey = 'web') {
         // 该接口不改变 refer 信息
        if (typeof url === 'object' && url.page) {
            if (this.currentUrlPackage && url.page === this.currentUrlPackage.page) {
                return this.currentUrlPackage.update(url.page, url.params);
            }
        }
        this.currentUrlPackage = new UrlPackage(url as UrlObj, type);
    }

    /**
     * 更新 refer url 信息
     * @param url
     * @param type
     */
    updateReferUrlPackage(url: UrlObj | UrlPackage | string = this.currentUrlPackage, type: PageTypeKey = 'web') {
        if (url instanceof UrlPackage) {
            this.referUrlPackage = url;
        } else {
            this.referUrlPackage = new UrlPackage(url as UrlObj, type);
        }
    }
    /**
     * 重设 common_package 信息
     * @param base
     */
    updateBase(base: HttpCommonPackage) {
        this.updateCommonPackage(base);
    }
    /**
     * 更新现有 common_package 信息
     */
    updateCommonPackage(options: HttpCommonPackage | CommonPackageOptions) {
        if (typeof options !== 'object') return;
        this.commonPackage.update(options);
    }

    /**
     * 初始化插件
     * @param plugins
     */
    addPlugins() {
        if (!this.logConfig.plugins || !this.logConfig.plugins.length) return;
        // 类似 webpack plugins 的实例数组
        this.logConfig.plugins.forEach((plugin: any) => {
            if (typeof plugin === 'object' && typeof plugin.apply === 'function') {
                this.addPluginInstance(plugin);
            }
        });
    }
    /**
     * 加载插件实例
     */
    addPluginInstance(pluginInstance: any) {
        if (pluginInstance) {
            const key = pluginInstance.key || pluginInstance.constructor && pluginInstance.constructor.key || `plugin_auto_key_${pluginIndex++}`;
            if (typeof pluginInstance.apply === 'function'
                && (!pluginInstance.weblog || pluginInstance.weblog !== this)
            ) {
                pluginInstance.apply(this);
            }
            this.plugins[key] = pluginInstance;
        }
    }
    /**
     * 加载插件构造函数
     * @param plugin
     * @param pluginOptions
     */
    plug(plugin: BasePluginConstructor, pluginOptions?: any) {
        if (this.plugins[plugin.key]) {
            return error('[code 301]', `${plugin.key} 插件重复加载！`)
        }
        this.addPluginInstance(new plugin(this, pluginOptions));
    }

    unplug(name: string) {
        const plugin = this.plugins[name];
        if (plugin) {
            plugin.destroy();
            delete this.plugins[name];
        }
    }

    unplugAll() {
        for (const key in this.plugins) {
            if (this.plugins[key]) {
                this.unplug(key);
            }
        }
    }

    public flush = () => {
        this.logger.flush();
    }
    /**
     * 扩展类实现
     * @param eventType
     * @param eventOptions
     * @returns
     */
    generateLog<T extends IEventType>(
        eventType: T,
        eventOptions?: IEventOptions<T>
    ) {
        return {};
    }
    send<T extends IEventType>(type: T, options: IEventOptions<T>, immediately?: boolean) {
        pageWarning(type, options, this.currentUrlPackage);
        const log = this.generateLog(type.toUpperCase() as T, options) as ClientLogV3.ReportEvent;
        // @ts-ignore
        if (typeof this.beforeSend === 'function') {
            // @ts-ignore
            this.beforeSend(type, options, log);
        }
        return this.logger.send(log, !!immediately, (options as ImmediatelyEventOptions<T>).callback);
    }
    /**
     * 收集上报
     * @param type
     * @param options
     * @returns
     */
    collect<T extends IEventType>(type: T, options: T extends UserEventType ? (undefined extends C[T] ? IEventOptions<T> : Omit<IEventOptions<T>, 'params'> & C[T]) : IEventOptions<T>) {
        this.send(type, options);
    }

    sendImmediately<T extends IEventType>(type: T, options: T extends UserEventType ? (undefined extends C[T] ? IEventOptions<T> : Omit<IEventOptions<T>, 'params'> & C[T]) : IEventOptions<T>): void {
        this.send(type, options, true);
    }

    sendPackage(data: LogPackage, callback?: (error?: any) => void) {
        this.logger.sendPackage(data, callback);
    }

    destroy() {
        this.unplugAll();
    }
}
